import { useState, useEffect, useCallback } from 'react';
import { 
  Trash2, 
  Building,
  CheckCircle2,
  Volume2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';

const ConsultoriosSettings = () => {
  const [consultorios, setConsultorios] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', professional_name: '' });
  const { addNotification } = useNotifications();

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('consultorios')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      setConsultorios(data || []);
    } catch (err) {
      console.error(err);
      addNotification('Error al cargar consultorios', 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  const fetchProfessionals = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('full_name')
        .order('full_name', { ascending: true });
      if (error) throw error;
      setProfessionals(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchRooms();
      fetchProfessionals();
    });
  }, [fetchRooms, fetchProfessionals]);

  const handleSave = async () => {
    if (!newRoom.name.trim()) {
      addNotification('El nombre es obligatorio', 'warning');
      return;
    }
    try {
      const { error } = await supabase
        .from('consultorios')
        .insert([{
          name: newRoom.name.trim(),
          professional_name: newRoom.professional_name || null
        }]);
      if (error) throw error;
      addNotification('Consultorio guardado correctamente', 'success');
      setNewRoom({ name: '', professional_name: '' });
      setShowForm(false);
      fetchRooms();
    } catch (err) {
      addNotification('Error al guardar: ' + err.message, 'error');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar el consultorio "${name}"?`)) return;
    try {
      const { error } = await supabase.from('consultorios').delete().eq('id', id);
      if (error) throw error;
      addNotification('Consultorio eliminado', 'info');
      fetchRooms();
    } catch (err) {
      console.error(err);
      addNotification('Error al eliminar consultorio', 'error');
    }
  };

  return (
    <div className="settings-section animate-fade">
      <div className="flex-between" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', margin: 0, fontSize: '1.4rem' }}>
            <Volume2 size={24} style={{ color: '#00e5ff' }} /> Listado de Consultorios
          </h3>
          <p className="section-desc" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
            Administrá los consultorios físicos o boxes a donde se llamará a los pacientes en el televisor.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'CANCELAR' : 'NUEVO CONSULTORIO +'}
        </button>
      </div>

      {showForm && (
        <div className="reason-form-card card glass-panel-premium animate-slide-in" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1.25rem', alignItems: 'end' }}>
            <div className="form-group">
              <label>Nombre del Consultorio</label>
              <input 
                type="text" 
                placeholder="Ej: N° 1 PB, Consultorio 3..."
                value={newRoom.name}
                onChange={(e) => setNewRoom({...newRoom, name: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Módulo / Profesional por Defecto (Opcional)</label>
              <select 
                value={newRoom.professional_name}
                onChange={(e) => setNewRoom({...newRoom, professional_name: e.target.value})}
              >
                <option value="">Ninguno / General</option>
                {professionals.map((prof, idx) => (
                  <option key={idx} value={prof.full_name}>{prof.full_name}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" onClick={handleSave} style={{ height: '42px', padding: '0 1.5rem' }}>
              GUARDAR
            </button>
          </div>
        </div>
      )}

      <div className="reasons-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
        {loading ? (
          <div className="text-center-p">Cargando consultorios...</div>
        ) : consultorios.length === 0 ? (
          <div className="text-center-p" style={{ color: 'rgba(255,255,255,0.3)', gridColumn: '1 / -1', padding: '2rem' }}>
            No hay consultorios configurados aún.
          </div>
        ) : consultorios.map(c => (
          <div 
            key={c.id} 
            className="reason-item card glass-panel" 
            style={{ 
              padding: '1.25rem', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              borderLeft: '4px solid #00e5ff',
              background: 'rgba(15, 23, 42, 0.25)',
              borderRadius: '12px'
            }}
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div className="icon-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', borderRadius: '8px', background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff' }}>
                <Building size={18} />
              </div>
              <div>
                <div style={{ fontWeight: '700', color: 'white', fontSize: '1rem' }}>{c.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <CheckCircle2 size={12} style={{ color: c.professional_name ? '#66bb6a' : '#546e7a' }} />
                  {c.professional_name ? `Asociado a: ${c.professional_name}` : 'Disponible General'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn-report-action delete" 
                onClick={() => handleDelete(c.id, c.name)} 
                title="Eliminar"
                style={{ 
                  background: 'rgba(239, 83, 80, 0.1)', 
                  color: '#ef5350', 
                  border: 'none', 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '6px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  cursor: 'pointer' 
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConsultoriosSettings;
